import { createSelector, createSlice } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

// Cap the recent-search list so the persisted slice doesn't grow unbounded
// across sessions. 10 is enough to give users a useful history for the
// global map searchbar without bloating local storage.
export const RECENT_SEARCHES_MAX = 10;

const initialState = {
  selectedLocation: null,
  highlightedFeature: null,
  loading: false,
  // Newest-first list of recent search entries issued from the global
  // map searchbar. Each entry has the shape:
  //   { key, query, result? }
  // - `key`   stable dedup/removal handle (string)
  // - `query` the original typed query string
  // - `result` optional snapshot describing a clicked result (icon,
  //   label, coordinates, plus the original SearchResultItem so the
  //   click handler can re-dispatch the right action). Absent when the
  //   entry was created from just submitting a search.
  // Persisted across sessions (see persistConfig).
  recentSearches: [],
};

/**
 * Coerce a payload (legacy string or new entry-shaped object) into the
 * canonical { key, query, result? } entry. Returns null when the
 * payload doesn't have a usable query string.
 */
const normalizeRecentEntry = payload => {
  if (typeof payload === 'string') {
    const term = payload.trim();
    if (!term) return null;
    return { key: `q:${term.toLowerCase()}`, query: term };
  }
  if (!payload || typeof payload !== 'object') return null;
  const query = typeof payload.query === 'string' ? payload.query.trim() : '';
  if (!query) return null;
  const entry = { query };
  if (payload.result && typeof payload.result === 'object') {
    entry.result = payload.result;
  }
  // Caller-supplied keys win (they encode result identity), otherwise
  // fall back to a query-only key so plain text searches still
  // collapse case-insensitively.
  entry.key =
    typeof payload.key === 'string' && payload.key
      ? payload.key
      : `q:${query.toLowerCase()}`;
  return entry;
};

/**
 * Migrate any persisted legacy string entries to the new object shape
 * in-place. Idempotent — entries already in the new shape pass through
 * untouched. Drops anything that isn't a usable string/object.
 */
const migrateRecentSearches = list => {
  if (!Array.isArray(list)) return [];
  const migrated = [];
  const seen = new Set();
  for (const raw of list) {
    const entry = normalizeRecentEntry(raw);
    if (!entry) continue;
    if (seen.has(entry.key)) continue;
    seen.add(entry.key);
    migrated.push(entry);
  }
  return migrated;
};

export const gisSearchSlice = createSlice({
  name: 'gisSearch',
  initialState: initialState,
  reducers: {
    setSelectedLocation: (state, action) => {
      return {
        ...state,
        selectedLocation: action.payload,
      };
    },
    clearSelectedLocation: state => {
      return {
        ...state,
        selectedLocation: null,
      };
    },
    setHighlightedFeature: (state, action) => {
      return {
        ...state,
        highlightedFeature: action.payload,
      };
    },
    clearHighlightedFeature: state => {
      return {
        ...state,
        highlightedFeature: null,
      };
    },
    /**
     * Push an entry onto the front of `recentSearches`, de-duplicated by
     * `key` so re-issuing an earlier search (or re-clicking the same
     * result) just bumps it to the top instead of producing a second
     * copy.
     *
     * Accepts either:
     *  - a raw query string (legacy callers): wrapped into
     *    `{ key: 'q:<lowercase>', query }`.
     *  - an entry-shaped object `{ query, result?, key? }`. When `key`
     *    is omitted we derive it from the query string (so plain
     *    searches still collapse case-insensitively).
     *
     * Result-backed entries should pass an explicit `key` that encodes
     * the underlying record identity (e.g. `r:call:<callerId>`) so the
     * same record clicked twice doesn't sprout multiple history rows.
     */
    addRecentSearch: (state, action) => {
      const entry = normalizeRecentEntry(action.payload);
      if (!entry) return state;
      const existing = migrateRecentSearches(state.recentSearches);
      const filtered = existing.filter(item => item.key !== entry.key);
      return {
        ...state,
        recentSearches: [entry, ...filtered].slice(0, RECENT_SEARCHES_MAX),
      };
    },
    /**
     * Remove a single recent entry by key. Accepts a legacy query
     * string for compatibility with older callers — those are matched
     * against the `q:<lowercase>` key produced by the legacy add path.
     */
    removeRecentSearch: (state, action) => {
      const raw = action.payload;
      let key;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return state;
        key = `q:${trimmed.toLowerCase()}`;
      } else if (
        raw &&
        typeof raw === 'object' &&
        typeof raw.key === 'string'
      ) {
        key = raw.key;
      } else {
        return state;
      }
      const existing = migrateRecentSearches(state.recentSearches);
      return {
        ...state,
        recentSearches: existing.filter(item => item.key !== key),
      };
    },
    clearRecentSearches: state => {
      return {
        ...state,
        recentSearches: [],
      };
    },
  },
  extraReducers: builder => {
    builder
      .addCase('incidents/setSelectedAlertID', state => {
        // Preserve recent searches across alert switches; only the
        // ephemeral selection/highlight pieces should reset.
        return {
          ...initialState,
          recentSearches: migrateRecentSearches(state.recentSearches),
        };
      })
      .addCase('FORCED_LOGOUT_SUCCESS', () => {
        return initialState;
      })
      .addCase('LOGOUT_SUCCESS', () => {
        return initialState;
      })
      .addCase('SINATRA_LOGOUT', () => {
        return initialState;
      });
  },
});

export const {
  setSelectedLocation,
  setHighlightedFeature,
  addRecentSearch,
  removeRecentSearch,
} = gisSearchSlice.actions;

// Explicitly re-export the no-payload action creators with a typed
// signature. Without this, TS infers `slice.actions.<reducer>` from a
// JS reducer of shape `state => ...` as a creator that requires an
// argument, producing `Expected at least 1 arguments, but got 0` at
// every `dispatch(clear*())` call site.
/** @type {import('@reduxjs/toolkit').ActionCreatorWithoutPayload} */
export const clearSelectedLocation =
  gisSearchSlice.actions.clearSelectedLocation;
/** @type {import('@reduxjs/toolkit').ActionCreatorWithoutPayload} */
export const clearHighlightedFeature =
  gisSearchSlice.actions.clearHighlightedFeature;
/** @type {import('@reduxjs/toolkit').ActionCreatorWithoutPayload} */
export const clearRecentSearches = gisSearchSlice.actions.clearRecentSearches;

// Memoized so the migration mapping doesn't churn referential equality
// across renders — useSelector subscribers stay stable until the
// underlying slice array actually changes.
export const selectRecentSearches = createSelector(
  state => state?.gisSearch?.recentSearches,
  list => migrateRecentSearches(list),
);

const persistConfig = {
  key: 'gisSearch',
  storage,
  // `recentSearches` is intentionally NOT blacklisted so it survives
  // page reloads / session restarts via redux-persist.
  blacklist: ['selectedLocation', 'highlightedFeature'],
};
const persistedReducer = persistReducer(persistConfig, gisSearchSlice.reducer);

export default persistedReducer;
